// ErrorBoundary — the app's safety net against the white screen of
// death. React unmounts the WHOLE tree when any descendant throws
// during render; without a boundary that means a silent blank page
// with zero diagnostic. This catches the throw and renders the error
// VERBATIM instead (beta-transparency rule: surface failure reasons,
// never swallow them).
//
// Usage:
//   <ErrorBoundary label="kids-mode" onReset={() => setActiveKidId(null)}>
//     <KidModeShell ... />
//   </ErrorBoundary>
//
//   label    — short id shown in the fallback + console so we know
//              which boundary caught it.
//   onReset  — optional. Renders a secondary "Go back" button that
//              calls this (e.g. exit kids mode) instead of a hard
//              reload. The boundary clears its own error state first
//              so the subtree re-mounts.
//   children — the protected subtree.

import { Component } from "react";
import { FONT, COLOR } from "../lib/tokens";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Loud, grep-friendly console trail. The component stack tells us
    // exactly which descendant threw — the thing the blank screen hid.
    // eslint-disable-next-line no-console
    console.error(
      `[ErrorBoundary:${this.props.label || "root"}] caught render throw:`,
      error,
      info && info.componentStack,
    );
    this.setState({ info });
  }

  handleReset = () => {
    this.setState({ error: null, info: null });
    if (typeof this.props.onReset === "function") this.props.onReset();
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const message = error && (error.message || String(error));
    const stack = (info && info.componentStack) || (error && error.stack) || "";

    return (
      <div
        role="alert"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 99999,
          background: COLOR.night,
          color: COLOR.ink,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "max(24px, env(safe-area-inset-top)) 20px max(24px, env(safe-area-inset-bottom))",
          boxSizing: "border-box",
          overflowY: "auto",
        }}
      >
        <div style={{ width: "100%", maxWidth: 520 }}>
          <div
            style={{
              fontFamily: FONT.mono,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.14em",
              color: COLOR.rose,
              marginBottom: 10,
            }}
          >
            SOMETHING BROKE{this.props.label ? ` · ${String(this.props.label).toUpperCase()}` : ""}
          </div>

          <div
            style={{
              fontFamily: FONT.itemTitle,
              fontStyle: "italic",
              fontSize: 22,
              lineHeight: 1.25,
              color: COLOR.ink,
              marginBottom: 16,
            }}
          >
            This screen hit an error and stopped instead of going blank.
          </div>

          {/* Error message + component stack, verbatim — this is the
              diagnostic the silent white screen used to swallow. */}
          <pre
            style={{
              fontFamily: FONT.mono,
              fontSize: 12,
              lineHeight: 1.5,
              color: COLOR.ink,
              background: COLOR.ground,
              border: `1px solid ${COLOR.border}`,
              borderRadius: 10,
              padding: "12px 14px",
              margin: 0,
              maxHeight: "40vh",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {message}
            {stack ? `\n${stack}` : ""}
          </pre>

          <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mcm-focusable"
              style={{
                flex: "1 1 auto",
                minWidth: 140,
                padding: "13px 20px",
                background: COLOR.leaf,
                color: "#fff",
                border: "none",
                borderRadius: 999,
                fontFamily: FONT.mono,
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: "0.1em",
                cursor: "pointer",
                touchAction: "manipulation",
              }}
            >
              RELOAD APP
            </button>
            {typeof this.props.onReset === "function" && (
              <button
                type="button"
                onClick={this.handleReset}
                className="mcm-focusable"
                style={{
                  flex: "1 1 auto",
                  minWidth: 140,
                  padding: "13px 20px",
                  background: "transparent",
                  color: COLOR.ink,
                  border: `1px solid ${COLOR.border}`,
                  borderRadius: 999,
                  fontFamily: FONT.mono,
                  fontSize: 13,
                  fontWeight: 800,
                  letterSpacing: "0.1em",
                  cursor: "pointer",
                  touchAction: "manipulation",
                }}
              >
                GO BACK
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
